import React, { useEffect, useState } from 'react'
import {
  CompanyId,
  GetCompanyEmployees$Request,
  GetCompanyStatisticsByPeriod$Request,
  Role,
  SetCompanyEmployees$Request,
  Stats,
  UserCompanyDto,
} from '../../services/api.types'
import { t } from 'ttag'
import dayjs from '../../dayjsConfig'
import StatCard from './components/StatCard/StatCard'
import { Dialog } from '@capacitor/dialog'
import { getShortDateWithTimeString } from '../../common/helpers/date'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { tokens } from '../../styles/tokens'

export interface IDashboardStateProps {
  companyId: CompanyId
  month: string
  employees: { [companyId: CompanyId]: number }
  companyDashboard: Stats[]
}

export interface IDashboardDispatchProps {
  getSpecificCompanyDashboard(payload: GetCompanyStatisticsByPeriod$Request): void
  getCompanyEmployees(payload: GetCompanyEmployees$Request): void
  setCompanyEmployees(payload: SetCompanyEmployees$Request & GetCompanyStatisticsByPeriod$Request): void
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
}

type IDashboardProps = IDashboardStateProps & IDashboardDispatchProps

const useStyles = makeStyles((theme) => ({
  header: {
    textAlign: 'left',
    marginBottom: '40px',
    [theme.breakpoints.up('md')]: {
      textAlign: 'right',
      marginBottom: '0px',
    },
  },
  h2: {
    fontWeight: 700,
    fontSize: '1.5rem',
    //lineHeight: '29px',
    lineHeight: '1.8125rem',
    marginTop: '0px',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'none',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
}))

export const selectUserCompany = (companies: UserCompanyDto[], selectedCompanyId: CompanyId | null): UserCompanyDto => {
  if (selectedCompanyId) {
    const uc = companies.find((uc) => uc.companyId === selectedCompanyId)
    return uc ? uc : companies[0]
  }
  return companies[0]
}

export default function Dashboard(props: IDashboardProps) {
  const classes = useStyles()
  const {
    companyId,
    month,
    employees,
    companyDashboard,
    getSpecificCompanyDashboard,
    getCompanyEmployees,
    setCompanyEmployees,
    hasRoleInCompany,
  } = props

  const employeeCount = employees ? employees[companyId] : false

  const [statsSelectedMonth, setStatsSelectedMonth] = useState([] as any)
  const [statsPreviousMonth, setStatsPreviousMonth] = useState([] as any)

  useEffect(() => {
    const currentMoment = dayjs(`${month}-01`).locale('en')
    const selectedMonthStartDate = currentMoment.clone().format('YYYY-MM-DD')
    const previousMonthStartDate = currentMoment.clone().subtract(1, 'month').format('YYYY-MM-DD')
    setStatsSelectedMonth(
      companyDashboard
        .filter((stat) => stat.startDate === selectedMonthStartDate)
        .reduce((obj, item) => Object.assign(obj, { [item.type]: item }), {}),
    )
    setStatsPreviousMonth(
      companyDashboard
        .filter((stat) => stat.startDate === previousMonthStartDate)
        .reduce((obj, item) => Object.assign(obj, { [item.type]: item }), {}),
    )
  }, [companyDashboard])

  const updateData = () => {
    const selectedMonth = dayjs(`${month}-01`)
    const startDate = selectedMonth.clone().subtract(1, 'months').locale('en').format('YYYY-MM-DD')
    const endDate = selectedMonth.clone().endOf('month').locale('en').format('YYYY-MM-DD')
    getSpecificCompanyDashboard({
      companyId,
      period: 'MONTH',
      startDate,
      endDate,
    })
  }

  useEffect(() => {
    updateData()
  }, [companyId, month])

  useEffect(() => {
    if (hasRoleInCompany(companyId, Role.ADMIN)) {
      getCompanyEmployees({ companyId })
    }
  }, [companyId])

  const getStats = (type: string, type2?: string, type3?: string) => {
    const current = statsSelectedMonth[type] || {
      type,
      result: null,
    }
    const previous = statsPreviousMonth[type] || {
      type,
      result: null,
    }
    if (!type2 || !type3) {
      return {
        current,
        previous,
      }
    } else {
      return {
        current,
        previous,
        current2: statsSelectedMonth[type2] || {
          type: type2,
          result: null,
        },
        previous2: statsSelectedMonth[type3] || {
          type: type3,
          result: null,
        },
      }
    }
  }

  const editEmployees = async () => {
    const formattedMonth = dayjs(month).format('MMM YYYY')
    const { value, cancelled } = await Dialog.prompt({
      title: '',
      message: `${t`Employees`} (${formattedMonth})`,
      inputText: employeeCount ? String(employeeCount) : '',
    })
    if (cancelled) return

    const selectedMonth = dayjs(`${month}-01`)
    const startDate = selectedMonth.clone().subtract(1, 'months').locale('en').format('YYYY-MM-DD')
    const endDate = selectedMonth.clone().endOf('month').locale('en').format('YYYY-MM-DD')
    setCompanyEmployees({
      companyId,
      employees: parseInt(value),
      month,

      period: 'MONTH',
      startDate,
      endDate,
    })
  }

  const mostAdvancedUsers = getStats('COMPANY_MOST_ADVANCED_USERS')
  const mostEngagedUsers = getStats('COMPANY_MOST_ENGAGED_USERS')
  const mostActiveUsers = getStats('COMPANY_MOST_ACTIVE_USERS')
  const showTop10 = mostAdvancedUsers.current.data || mostEngagedUsers.current.data || mostActiveUsers.current.data

  const lastUpdated = statsSelectedMonth['COMPANY_TOTAL_USERS']?.updatedAt

  return (
    <>
      <h2 className={classes.h2}>{t`Users`}</h2>
      <div className={classes.grid} style={{ marginBottom: '32px' }}>
        <StatCard
          stats={getStats('COMPANY_EMPLOYEES')}
          editEmployees={hasRoleInCompany(companyId, Role.ADMIN) ? editEmployees : undefined}
        />
        <StatCard stats={getStats('COMPANY_TOTAL_USERS')} />
        <StatCard stats={getStats('COMPANY_ACTIVE_USERS')} />

        <StatCard stats={getStats('COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE')} />
        <StatCard stats={getStats('COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE')} />
        <StatCard stats={getStats('COMPANY_HR_USERS_PERCENTAGE')} />

        {showTop10 ? (
          <>
            <StatCard stats={mostActiveUsers} />
            <StatCard stats={mostAdvancedUsers} />
            <StatCard stats={mostEngagedUsers} />
          </>
        ) : (
          <></>
        )}
      </div>
      <h2 className={classes.h2}>{t`Activity`}</h2>
      <div className={classes.grid}>
        <StatCard stats={getStats('COMPANY_STEPS_PER_USER_PER_DAY')} />
        <StatCard stats={getStats('COMPANY_EXEMPLARY_ACTIVE')} />
        <StatCard stats={getStats('COMPANY_MEDIAN_ACTIVITY')} />

        <StatCard stats={getStats('COMPANY_TOTAL_STEPS')} />
        <StatCard stats={getStats('COMPANY_TOTAL_CALORIES')} />
        <StatCard stats={getStats('COMPANY_TOTAL_MINUTES')} />

        <StatCard
          stats={getStats('COMPANY_MOST_ACTIVE_DAY', 'COMPANY_MOST_ACTIVE_DAY_MEDIAN', 'COMPANY_MEDIAN_ACTIVITY')}
        />
        <StatCard stats={getStats('COMPANY_CHALLENGES')} />
        <StatCard stats={getStats('COMPANY_CHALLENGES_PARTICIPANTS')} />
      </div>
      {lastUpdated && (
        // Previously opacity: 0.5 on $primary-text-color yielded #868FA8 (3.22:1).
        // secondaryText (#495881 = 7.01:1) keeps the muted look while passing AA.
        <div style={{ marginTop: 20, color: tokens.text.secondary, fontSize: '0.875rem' }}>
          {t`Updated`}: {getShortDateWithTimeString(lastUpdated)}
        </div>
      )}
    </>
  )
}
